import { useListRecentNotablePulls } from './queries';
import { PullEntry } from './PullEntry';
import { Skeleton } from '@/components/ui/skeleton';

export interface RecentPullsProps {
  layout: 'vertical' | 'horizontal';
}

/**
 * Recent notable pulls feed. Handles its own loading/loaded/empty states
 * so the BrowsePage layout shell is consistent regardless of the parent's
 * own loading state (CLS prevention).
 *
 * - isLoading → internal skeleton with reserved height matching PullEntry
 * - data resolved + non-empty → real content
 * - data resolved + empty → null (rare; synthetic pulls fire daily)
 */
export function RecentPulls({ layout }: RecentPullsProps): JSX.Element | null {
  const { data: pulls, isLoading } = useListRecentNotablePulls();

  if (isLoading) {
    return <RecentPullsLoading layout={layout} />;
  }

  if (!pulls || pulls.length === 0) {
    return null;
  }

  if (layout === 'horizontal') {
    return (
      <section aria-label="Recent notable pulls" data-testid="recent-pulls">
        <p className="text-small font-semibold text-text-1 uppercase tracking-wide mb-2">
          Recent Pulls
        </p>
        <div className="flex gap-2 overflow-x-auto pb-2" role="list">
          {pulls.map((pull, idx) => (
            <div key={`${pull.card_name}-${idx}`} role="listitem">
              <PullEntry pull={pull} layout="horizontal" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <aside aria-label="Recent notable pulls" data-testid="recent-pulls">
      <h2 className="text-h1 mb-4">Recent Pulls</h2>
      <div className="grid grid-cols-2 gap-2" role="list">
        <div className="flex flex-col gap-2">
          {pulls.slice(0, 5).map((pull, idx) => (
            <div key={`${pull.card_name}-${idx}`} role="listitem">
              <PullEntry pull={pull} layout="vertical" />
            </div>
          ))}
        </div>
        {pulls.length > 5 && (
          <div className="flex flex-col gap-2">
            {pulls.slice(5, 10).map((pull, idx) => (
              <div key={`${pull.card_name}-${idx + 5}`} role="listitem">
                <PullEntry pull={pull} layout="vertical" />
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function RecentPullsLoading({
  layout,
}: {
  layout: 'vertical' | 'horizontal';
}): JSX.Element {
  if (layout === 'horizontal') {
    return (
      <section
        aria-label="Recent notable pulls (loading)"
        data-testid="recent-pulls-skeleton"
      >
        <Skeleton className="h-3 w-24 mb-2" />
        <div className="flex gap-2 overflow-hidden pb-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              className="aspect-[490/684] w-[120px] shrink-0 rounded-lg"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <aside
      aria-label="Recent notable pulls (loading)"
      data-testid="recent-pulls-skeleton"
    >
      <Skeleton className="h-7 w-32 mb-4" />
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[490/684] w-full rounded-lg" />
          ))}
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[490/684] w-full rounded-lg" />
          ))}
        </div>
      </div>
    </aside>
  );
}
